// Hardening contro estensioni browser che mutano il DOM (password manager,
// Grammarly, traduttori, ad-blocker, ecc.). Quando un'estensione rimuove o
// sposta un nodo gestito da un portal Radix, React in fase di commitDeletion
// chiama removeChild su un nodo il cui parent non è più quello atteso →
// `NotFoundError: Failed to execute 'removeChild' on 'Node'` che, intercettato
// dall'ErrorBoundary, fa crashare l'intera vista. Sintomo tipico: prod-only +
// desktop-only (le estensioni non girano nell'iframe di preview né su mobile),
// sparisce in incognito.
//
// Lo shim NON pre-filtra le chiamate: esegue sempre il metodo nativo e
// intercetta SOLO la `DOMException` specifica di questa famiglia di crash quando
// il nodo di riferimento non appartiene più al parent atteso. Qualsiasi altro
// errore viene rilanciato, così non si maschera mai un bug DOM legittimo.
// Workaround canonico per React issue #11538.

type RemoveChildFn = <T extends Node>(child: T) => T;
type InsertBeforeFn = <T extends Node>(node: T, child: Node | null) => T;

let installed = false;

function isDomException(err: unknown, names: readonly string[]): boolean {
  return err instanceof DOMException && names.includes(err.name);
}

export function installDomMutationGuard(): void {
  if (installed) return;
  if (typeof Node !== 'function' || !Node.prototype) return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild as RemoveChildFn;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    try {
      return originalRemoveChild.call(this, child) as T;
    } catch (err) {
      if (child.parentNode !== this && isDomException(err, ['NotFoundError'])) {
        if (import.meta.env.DEV) {
          console.warn('[dom-guard] removeChild su nodo non più figlio (estensione DOM?)', child);
        }
        return child;
      }
      throw err;
    }
  } as RemoveChildFn;

  const originalInsertBefore = Node.prototype.insertBefore as InsertBeforeFn;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    try {
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    } catch (err) {
      if (
        referenceNode &&
        referenceNode.parentNode !== this &&
        isDomException(err, ['NotFoundError', 'HierarchyRequestError'])
      ) {
        if (import.meta.env.DEV) {
          console.warn('[dom-guard] insertBefore con reference orfana (estensione DOM?)', referenceNode);
        }
        return originalInsertBefore.call(this, newNode, null) as T;
      }
      throw err;
    }
  } as InsertBeforeFn;
}
